import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Button, Space, Input, Select, Modal, Form, Switch, Tag, Tooltip, Descriptions, message } from 'antd';
import { SearchOutlined, StarFilled, StarOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import request from '../../../utils/request';
import { useAuth } from '../../../hooks/useAuth';
import { COUNTRY_OPTIONS } from '../shipping-rates/AddressFields';
import IdPhotoUploader from './IdPhotoUploader';
import IdPhotoThumb from './IdPhotoThumb';
import { mimeToExt, downloadObjectUrl } from './imageUtils';
import {
  getCustomerAddresses,
  createCustomerAddress,
  updateCustomerAddress,
  deleteCustomerAddress,
  setCustomerAddressDefault,
  downloadCustomerAddressIdPhoto,
} from '../../../services/logistics';
import type { CustomerAddress, CustomerAddressFormValues } from '../../../services/logistics';

const ADDRESS_TYPE_OPTIONS = [
  { value: 'sender', label: '作为发件人' },
  { value: 'recipient', label: '作为收件人' },
  { value: 'both', label: '作为发件人或收件人' },
];
const addressTypeLabel = (v?: string) => ADDRESS_TYPE_OPTIONS.find((o) => o.value === v)?.label || v || '-';
const addressTypeColor = (v?: string) => (v === 'sender' ? 'blue' : v === 'recipient' ? 'orange' : v === 'both' ? 'green' : 'default');

const ID_TYPE_OPTIONS = [
  { value: 'id_card', label: '身份证' },
  { value: 'passport', label: '护照' },
  { value: 'ssn', label: 'SSN' },
  { value: 'ein', label: 'EIN' },
  { value: 'other', label: '其他' },
];

const countryLabel = (v?: string) => COUNTRY_OPTIONS.find((o) => o.value === v)?.label || v || '-';

const emptyFormValues: CustomerAddressFormValues = {
  address_type: 'both',
  country: 'US',
  postal_code: '',
  state: '',
  city: '',
  address_line: '',
  name: '',
  phone: '',
  is_active: true,
};

const CustomerAddressBookPage: React.FC = () => {
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  const isCustomer = user?.role === 'customer';
  const canPickCustomer = !isCustomer; // 管理员/员工代客户创建，需要选择所属客户

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CustomerAddress[]>([]);
  const [searchText, setSearchText] = useState('');
  const [fltAddressType, setFltAddressType] = useState<string | undefined>(undefined);
  // 默认关闭：只显示启用中的地址，打开后不按状态过滤（启用+停用都显示）
  const [fltShowInactive, setFltShowInactive] = useState(false);
  const [fltCustomerId, setFltCustomerId] = useState<number | undefined>(undefined);
  const [fltCountry, setFltCountry] = useState<string | undefined>(undefined);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({ current: 1, pageSize: 10, total: 0 });

  const [customerOptions, setCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [optsLoading, setOptsLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingRow, setEditingRow] = useState<CustomerAddress | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [idPhotoFront, setIdPhotoFront] = useState<File | null>(null);
  const [idPhotoBack, setIdPhotoBack] = useState<File | null>(null);

  const fetchCustomerOptions = async () => {
    if (!canPickCustomer) return;
    setOptsLoading(true);
    try {
      const resp = await request.get('/api/v1/users/', {
        params: { role: 'customer', page_size: 200, customer_sub_role: 'main_account', sub_role: 'main_account' },
      });
      const list: any[] = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      setCustomerOptions(list.map((u: any) => ({ value: Number(u.id), label: String(u.username || u.company_name || u.id) })));
    } catch {
      setCustomerOptions([]);
    } finally {
      setOptsLoading(false);
    }
  };

  const fetchData = async (
    p: TablePaginationConfig,
    q?: string,
    overrides?: Partial<{ address_type?: string; showInactive?: boolean; customer?: number; country?: string }>
  ) => {
    setLoading(true);
    try {
      const current = Number(p?.current || 1) || 1;
      const pageSize = Number(p?.pageSize || 10) || 10;
      const address_type = overrides && 'address_type' in overrides ? overrides.address_type : fltAddressType;
      const showInactive = overrides && 'showInactive' in overrides ? overrides.showInactive : fltShowInactive;
      const customer = overrides && 'customer' in overrides ? overrides.customer : fltCustomerId;
      const country = overrides && 'country' in overrides ? overrides.country : fltCountry;
      const resp = await getCustomerAddresses({
        page: current,
        page_size: pageSize,
        search: q || undefined,
        address_type: address_type || undefined,
        // 默认（showInactive=false）只显示启用中的地址；打开"显示已停用"后不按状态过滤
        is_active: showInactive ? undefined : true,
        customer: customer != null ? Number(customer) : undefined,
        country: country || undefined,
      });
      const body = resp.data as any;
      const rows: CustomerAddress[] = Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []);
      const total = Number(body?.count ?? rows.length) || 0;
      setData(rows);
      setPagination((prev) => ({ ...prev, current, pageSize, total }));
    } catch (e: any) {
      message.error('获取地址库列表失败');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchCustomerOptions();
      fetchData(pagination, searchText);
    })();
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => fetchData({ ...pagination, current: 1 }, searchText);

  // 通用搜索框：输入即搜索，防抖 400ms，避免每敲一个字就打一次请求
  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchData({ ...pagination, current: 1 }, value);
    }, 400);
  };

  const handleResetFilters = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setFltAddressType(undefined);
    setFltShowInactive(false);
    setFltCustomerId(undefined);
    setFltCountry(undefined);
    setSearchText('');
    fetchData({ ...pagination, current: 1 }, undefined, { address_type: undefined, showInactive: false, customer: undefined, country: undefined });
  };

  const openCreateModal = () => {
    setModalMode('create');
    setEditingRow(null);
    setIdPhotoFront(null);
    setIdPhotoBack(null);
    setModalOpen(true);
    setTimeout(() => {
      form.resetFields();
      form.setFieldsValue(emptyFormValues);
    }, 0);
  };

  const openEditModal = (record: CustomerAddress) => {
    setModalMode('edit');
    setEditingRow(record);
    setIdPhotoFront(null);
    setIdPhotoBack(null);
    setModalOpen(true);
    setTimeout(() => {
      form.resetFields();
      form.setFieldsValue({
        customer: record.customer,
        label: record.label || '',
        address_type: record.address_type,
        country: record.country,
        postal_code: record.postal_code,
        state: record.state,
        city: record.city,
        address_line: record.address_line,
        address_line2: record.address_line2 || '',
        name: record.name,
        company: record.company || '',
        phone: record.phone,
        email: record.email || '',
        remarks: record.remarks || '',
        id_type: record.id_type || undefined,
        id_number: record.id_number || undefined,
        is_active: record.is_active !== false,
      });
    }, 0);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload: CustomerAddressFormValues = { ...values };
      if (isStaff) {
        // 员工看不到证件信息，也不允许代客户写入/覆盖这两个字段（即使后端也会二次剥离，这里先不提交更省一次校验往返）
        delete (payload as any).id_type;
        delete (payload as any).id_number;
      }
      if (!canPickCustomer) {
        delete (payload as any).customer;
      }
      const files = { idPhotoFront, idPhotoBack };
      if (modalMode === 'edit' && editingRow?.id) {
        await updateCustomerAddress(Number(editingRow.id), payload, files);
        message.success('保存成功');
      } else {
        await createCustomerAddress(payload, files);
        message.success('创建成功');
      }
      setModalOpen(false);
      setEditingRow(null);
      await fetchData({ ...pagination, current: modalMode === 'create' ? 1 : pagination.current }, searchText);
    } catch (e: any) {
      if (e?.errorFields) return;
      const detail = e?.response?.data || e?.message || '操作失败';
      if (typeof detail === 'object') {
        Object.entries(detail).forEach(([k, v]) => message.error(`${k}: ${Array.isArray(v) ? v[0] : v}`));
      } else {
        message.error(String(detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (record: CustomerAddress) => {
    Modal.confirm({
      title: '确认删除该地址？',
      content: record.label || record.name,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteCustomerAddress(Number(record.id));
          message.success('已删除');
          const cur = Number(pagination.current || 1) || 1;
          const ps = Number(pagination.pageSize || 10) || 10;
          const rest = (data || []).length - 1;
          const nextPage = rest <= 0 && cur > 1 ? cur - 1 : cur;
          await fetchData({ ...pagination, current: nextPage, pageSize: ps }, searchText);
        } catch (e: any) {
          message.error(e?.response?.data?.detail || '删除失败');
        }
      },
    });
  };

  const handleSetDefault = async (record: CustomerAddress, field: 'is_default_origin' | 'is_default_recipient', value: boolean) => {
    try {
      const resp = await setCustomerAddressDefault(Number(record.id), field, value);
      const updated = resp.data;
      // 直接用接口返回的最新记录原地更新本地列表，不重新拉取整页——重新拉取会按 updated_at
      // 重新排序，把刚操作过的这几行顶到最前面，行的显示顺序会跟着跳动
      setData((prev) => prev.map((row) => {
        if (row.id === updated.id) return updated;
        // 设为默认时，后端会原子性地把同一客户名下其它记录的这个默认标记清掉，
        // 本地也同步一下，避免列表里同时出现两个"默认发件人"
        if (value && row.customer === updated.customer && (row as any)[field]) {
          return { ...row, [field]: false };
        }
        return row;
      }));
      message.success(value ? '已设为默认地址' : '已取消默认地址');
    } catch (e: any) {
      message.error(e?.response?.data?.error || '操作失败');
    }
  };

  // 立即删除已保存的证件照片：不经过弹窗确认，不需要点"保存"——
  // 只 PATCH remove_id_photo_front/back 这一个字段，不带表单里其它还没保存的改动
  const handleDeleteExistingPhoto = async (side: 'front' | 'back') => {
    if (!editingRow?.id) return;
    const field = side === 'front' ? 'remove_id_photo_front' : 'remove_id_photo_back';
    try {
      const resp = await updateCustomerAddress(Number(editingRow.id), { [field]: true } as Partial<CustomerAddressFormValues>);
      setEditingRow(resp.data);
      message.success('照片已删除');
      fetchData(pagination, searchText);
    } catch (e: any) {
      const detail = e?.response?.data;
      message.error((detail && (detail[side === 'front' ? 'id_photo_front' : 'id_photo_back'] || detail.detail)) || '删除照片失败');
      throw e;
    }
  };

  // 列表行内"正面"/"背面"快捷查看：弹窗预览，不新开标签页
  const [photoPreview, setPhotoPreview] = useState<{ open: boolean; url: string | null; title: string; ext: string }>({
    open: false, url: null, title: '', ext: 'jpg',
  });

  const handleViewPhoto = async (record: CustomerAddress, side: 'front' | 'back') => {
    const title = side === 'front' ? '证件正面照片' : '证件背面照片';
    setPhotoPreview({ open: true, url: null, title, ext: 'jpg' });
    try {
      const resp = await downloadCustomerAddressIdPhoto(Number(record.id), side);
      const blob = resp.data as Blob;
      const url = URL.createObjectURL(blob);
      setPhotoPreview({ open: true, url, title, ext: mimeToExt(blob.type) });
    } catch (e: any) {
      message.error(e?.response?.data?.error || '查看照片失败');
      setPhotoPreview({ open: false, url: null, title: '', ext: 'jpg' });
    }
  };

  const closePhotoPreview = () => {
    if (photoPreview.url) { try { URL.revokeObjectURL(photoPreview.url); } catch {} }
    setPhotoPreview({ open: false, url: null, title: '', ext: 'jpg' });
  };

  const handleDownloadPreview = () => {
    if (!photoPreview.url) return;
    downloadObjectUrl(photoPreview.url, `${photoPreview.title}.${photoPreview.ext}`);
  };

  // "查看"详情弹窗
  const [detailRow, setDetailRow] = useState<CustomerAddress | null>(null);

  const formatDateTime = (v?: string | null) => {
    if (!v) return '-';
    try { return new Date(v).toLocaleString(); } catch { return v; }
  };

  // 复制的纯文本信息：包含证件号码，但不包含图片（图片本来就没法当文字复制）
  const buildAddressCopyText = (r: CustomerAddress): string => {
    const idTypeLabel = r.id_type ? (ID_TYPE_OPTIONS.find((o) => o.value === r.id_type)?.label || r.id_type) : '';
    const idNumberText = r.id_number == null ? (r.id_type ? '无权限查看' : '') : (r.id_number || '');
    const lines = [
      `地址备注名：${r.label || '-'}`,
      `地址类型：${addressTypeLabel(r.address_type)}`,
      `联系人：${r.name || '-'}`,
      r.company ? `公司：${r.company}` : '',
      r.phone ? `电话：${r.phone}` : '',
      r.email ? `Email：${r.email}` : '',
      `国家/地区：${countryLabel(r.country)}`,
      `州/省：${r.state || '-'}`,
      `城市：${r.city || '-'}`,
      `邮编：${r.postal_code || '-'}`,
      `地址第一行：${r.address_line || '-'}`,
      r.address_line2 ? `地址第二行：${r.address_line2}` : '',
      idTypeLabel ? `证件类型：${idTypeLabel}` : '',
      idNumberText ? `证件号码：${idNumberText}` : '',
      r.remarks ? `备注信息：${r.remarks}` : '',
      `默认发件人：${r.is_default_origin ? '是' : '否'}`,
      `默认收件人：${r.is_default_recipient ? '是' : '否'}`,
      `状态：${r.is_active === false ? '停用' : '启用'}`,
      r.customer_name ? `所属客户：${r.customer_name}` : '',
      r.created_by_name ? `创建人：${r.created_by_name}` : '',
      `创建时间：${formatDateTime(r.created_at)}`,
      `更新时间：${formatDateTime(r.updated_at)}`,
    ].filter(Boolean);
    return lines.join('\n');
  };

  const handleCopyDetail = async () => {
    if (!detailRow) return;
    try {
      await navigator.clipboard.writeText(buildAddressCopyText(detailRow));
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };

  const columns: ColumnsType<CustomerAddress> = useMemo(() => ([
    { title: '备注名', dataIndex: 'label', width: 130, ellipsis: true, render: (v: any, r: any) => v || r?.name || '-' },
    {
      title: '地址类型',
      dataIndex: 'address_type',
      width: 170,
      render: (v: any) => <Tag color={addressTypeColor(v)}>{addressTypeLabel(v)}</Tag>,
    },
    {
      title: '联系人',
      dataIndex: 'name',
      width: 110,
      ellipsis: true,
      render: (v: any, r: any) => (
        <Tooltip
          title={(
            <div>
              <div>姓名：{r?.name || '-'}</div>
              {r?.company && <div>公司：{r.company}</div>}
              {r?.phone && <div>电话：{r.phone}</div>}
              {r?.email && <div>Email：{r.email}</div>}
            </div>
          )}
        >
          <span>{v || '-'}</span>
        </Tooltip>
      ),
    },
    { title: '国家/地区', dataIndex: 'country', width: 110, render: (v: any) => countryLabel(v) },
    { title: '州/省', dataIndex: 'state', width: 90 },
    { title: '城市', dataIndex: 'city', width: 110, ellipsis: true },
    { title: '邮编', dataIndex: 'postal_code', width: 90 },
    {
      title: '证件信息',
      key: 'id_info',
      width: 150,
      render: (_: any, r: any) => {
        let numberNode: React.ReactNode;
        if (!r?.id_type && !r?.id_number) {
          numberNode = <span style={{ color: '#bfbfbf' }}>-</span>;
        } else if (r?.id_number == null) {
          numberNode = <Tooltip title="无权限查看证件信息"><span style={{ color: '#bfbfbf' }}>无权限查看</span></Tooltip>;
        } else {
          numberNode = r.id_number;
        }
        const emptyHint = r?.id_type ? '无权限查看或未上传' : undefined;
        return (
          <div>
            <div style={{ marginBottom: 4 }}>{numberNode}</div>
            <Space size={4}>
              <IdPhotoThumb
                url={r?.id_photo_front_url}
                emptyHint={emptyHint}
                fetchBlob={() => downloadCustomerAddressIdPhoto(Number(r.id), 'front').then((resp) => resp.data as Blob)}
                onClick={() => handleViewPhoto(r, 'front')}
              />
              <IdPhotoThumb
                url={r?.id_photo_back_url}
                emptyHint={emptyHint}
                fetchBlob={() => downloadCustomerAddressIdPhoto(Number(r.id), 'back').then((resp) => resp.data as Blob)}
                onClick={() => handleViewPhoto(r, 'back')}
              />
            </Space>
          </div>
        );
      },
    },
    {
      title: '设为默认地址',
      key: 'defaults',
      width: 190,
      render: (_: any, r: any) => (
        <Space size={4} wrap>
          <Tooltip title={r?.is_default_origin ? '取消默认发件人' : '设为默认发件人'}>
            <Button
              size="small"
              icon={r?.is_default_origin ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
              onClick={() => handleSetDefault(r, 'is_default_origin', !r?.is_default_origin)}
            >
              {r?.is_default_origin ? '默认发件人' : '发件人'}
            </Button>
          </Tooltip>
          <Tooltip title={r?.is_default_recipient ? '取消默认收件人' : '设为默认收件人'}>
            <Button
              size="small"
              icon={r?.is_default_recipient ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
              onClick={() => handleSetDefault(r, 'is_default_recipient', !r?.is_default_recipient)}
            >
              {r?.is_default_recipient ? '默认收件人' : '收件人'}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
    { title: '状态', dataIndex: 'is_active', width: 80, render: (v: any) => (v === false ? <Tag color="red">停用</Tag> : <Tag color="green">启用</Tag>) },
    ...(canPickCustomer ? [{ title: '所属客户', dataIndex: 'customer_name', width: 120, ellipsis: true, render: (v: any, r: any) => v || (r?.customer ? `#${r.customer}` : '-') } as any] : []),
    {
      title: '操作',
      key: 'ops',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, r: any) => (
        <Space size={6}>
          <Button size="small" onClick={() => setDetailRow(r)}>查看</Button>
          <Button size="small" onClick={() => openEditModal(r)}>编辑</Button>
          <Button size="small" danger onClick={() => handleDelete(r)}>删除</Button>
        </Space>
      ),
    },
  ]), [data, canPickCustomer]);

  const idFieldsDisabled = isStaff;

  return (
    <div className="p-6">
      <Card
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span>地址库</span>
              <span style={{ display: 'inline-block', padding: '2px 8px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                共 {Number(pagination?.total || 0)} 条记录
              </span>
            </div>
            <Button type="primary" onClick={openCreateModal}>新建地址</Button>
          </div>
        )}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <Select
            style={{ width: 160 }}
            placeholder="地址类型"
            allowClear
            value={fltAddressType}
            options={ADDRESS_TYPE_OPTIONS}
            onChange={(v) => { setFltAddressType(v); fetchData({ ...pagination, current: 1 }, searchText, { address_type: v }); }}
          />
          <Select
            style={{ width: 160 }}
            placeholder="国家/地区"
            allowClear
            showSearch
            value={fltCountry}
            options={COUNTRY_OPTIONS}
            optionFilterProp="label"
            filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
            onChange={(v) => {
              const val = v || undefined;
              setFltCountry(val);
              fetchData({ ...pagination, current: 1 }, searchText, { country: val });
            }}
          />
          {canPickCustomer && (
            <Select
              style={{ width: 200 }}
              placeholder="所属客户"
              allowClear
              showSearch
              loading={optsLoading}
              value={fltCustomerId}
              options={customerOptions}
              optionFilterProp="label"
              filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
              onChange={(v) => {
                const val = typeof v === 'number' && Number.isFinite(v) ? v : undefined;
                setFltCustomerId(val);
                fetchData({ ...pagination, current: 1 }, searchText, { customer: val });
              }}
            />
          )}
          <Input
            placeholder="搜索备注名/联系人/公司/城市/邮编"
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
            value={searchText}
            onChange={(e) => handleSearchTextChange(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
          />
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch
              checked={fltShowInactive}
              onChange={(checked) => {
                setFltShowInactive(checked);
                fetchData({ ...pagination, current: 1 }, searchText, { showInactive: checked });
              }}
            />
            <span>显示已停用</span>
          </span>
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button onClick={handleResetFilters}>重置</Button>
        </div>

        <Table<CustomerAddress>
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          onChange={(p) => fetchData(p, searchText)}
          pagination={{ ...pagination, showSizeChanger: true, showQuickJumper: true, showTotal: (total, range) => `共${total}条记录，显示${range[0]}-${range[1]}条` }}
          scroll={{ x: 1500 }}
        />
      </Card>

      <Modal
        title={modalMode === 'edit' ? '编辑地址' : '新建地址'}
        open={modalOpen}
        onCancel={() => { if (!submitting) setModalOpen(false); }}
        okText={modalMode === 'edit' ? '保存' : '创建'}
        cancelText="取消"
        confirmLoading={submitting}
        onOk={handleSubmit}
        width={800}
      >
        <Form form={form} layout="vertical" initialValues={emptyFormValues}>
          {canPickCustomer && (
            <Form.Item label="所属客户" name="customer" rules={[{ required: true, message: '请选择所属客户' }]}>
              <Select
                showSearch
                loading={optsLoading}
                options={customerOptions}
                placeholder="选择客户"
                filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          )}
          <Space style={{ width: '100%' }} size={12}>
            <Form.Item label="地址类型" name="address_type" rules={[{ required: true }]} style={{ width: 180 }}>
              <Select options={ADDRESS_TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item label="地址备注名" name="label" style={{ flex: 1 }}>
              <Input maxLength={100} placeholder="如：总部仓库、常用收件人-张三" />
            </Form.Item>
          </Space>

          <Space style={{ width: '100%' }} size={12}>
            <Form.Item label="联系人姓名" name="name" rules={[{ required: true, message: '请输入联系人姓名' }]} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item label="公司名称（可选）" name="company" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space>

          <Form.Item label="地址第一行" name="address_line" rules={[{ required: true, message: '请输入详细地址' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="地址第二行（可选）" name="address_line2">
            <Input />
          </Form.Item>

          <Space style={{ width: '100%' }} size={12}>
            <Form.Item label="城市" name="city" rules={[{ required: true, message: '请输入城市' }]} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item label="州/省" name="state" rules={[{ required: true, message: '请输入州/省' }]} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} size={12}>
            <Form.Item label="邮编" name="postal_code" rules={[{ required: true, message: '请输入邮编' }]} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item label="国家/地区" name="country" rules={[{ required: true, message: '请选择国家/地区' }]} style={{ width: 180 }}>
              <Select options={COUNTRY_OPTIONS} showSearch optionFilterProp="label" />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} size={12}>
            <Form.Item label="电话（可选）" name="phone" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item label="Email（可选）" name="email" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space>
          <Form.Item label="备注（可选）" name="remarks">
            <Input.TextArea rows={2} maxLength={500} placeholder="取件注意事项、门禁密码之类的自由文本说明" />
          </Form.Item>

          {idFieldsDisabled ? (
            <Form.Item label="证件信息">
              <div style={{ color: '#bfbfbf' }}>员工账号无权查看或修改证件号码/证件照片</div>
            </Form.Item>
          ) : (
            <>
              <Space style={{ width: '100%' }} size={12}>
                <Form.Item label="证件类型（可选）" name="id_type" style={{ width: 180 }}>
                  <Select options={ID_TYPE_OPTIONS} allowClear placeholder="部分清关渠道需要" />
                </Form.Item>
                <Form.Item label="证件号码（可选）" name="id_number" style={{ flex: 1 }}>
                  <Input placeholder="身份证号/护照号/SSN/EIN 等" />
                </Form.Item>
              </Space>
              <Space style={{ width: '100%' }} size={24} align="start">
                <IdPhotoUploader
                  label="证件正面照片（可选）"
                  stagedFile={idPhotoFront}
                  onStagedFileChange={setIdPhotoFront}
                  existingUrl={editingRow?.id_photo_front_url}
                  fetchBlob={() => downloadCustomerAddressIdPhoto(Number(editingRow!.id), 'front').then((r) => r.data as Blob)}
                  deleteExisting={() => handleDeleteExistingPhoto('front')}
                />
                <IdPhotoUploader
                  label="证件背面照片（可选）"
                  stagedFile={idPhotoBack}
                  onStagedFileChange={setIdPhotoBack}
                  existingUrl={editingRow?.id_photo_back_url}
                  fetchBlob={() => downloadCustomerAddressIdPhoto(Number(editingRow!.id), 'back').then((r) => r.data as Blob)}
                  deleteExisting={() => handleDeleteExistingPhoto('back')}
                />
              </Space>
            </>
          )}

          <Form.Item label="是否启用" name="is_active" valuePropName="checked" style={{ marginTop: 30 }}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={photoPreview.open}
        onCancel={closePhotoPreview}
        title={photoPreview.title}
        centered
        // 这个弹窗可能是从"查看详情"弹窗里点缩略图打开的，两个 Modal 在 JSX 里是兄弟节点、
        // 不是嵌套关系，antd 的自动层叠层级只跟着组件树走，识别不出这层"从另一个弹窗里打开"的
        // 关系，所以这里手动给一个更高的 zIndex，保证它始终盖在详情弹窗上面
        zIndex={1050}
        footer={[
          <Button key="download" disabled={!photoPreview.url} onClick={handleDownloadPreview}>下载</Button>,
          <Button key="close" onClick={closePhotoPreview}>关闭</Button>,
        ]}
      >
        {photoPreview.url ? (
          <img src={photoPreview.url} alt={photoPreview.title} style={{ width: '100%' }} />
        ) : (
          <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>加载中...</div>
        )}
      </Modal>

      <Modal
        open={!!detailRow}
        onCancel={() => setDetailRow(null)}
        footer={null}
        width={720}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24 }}>
            <span>地址详情</span>
            <Button size="small" icon={<CopyOutlined />} onClick={handleCopyDetail}>复制完整信息</Button>
          </div>
        )}
      >
        {detailRow && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="地址类型">
              <Tag color={addressTypeColor(detailRow.address_type)}>{addressTypeLabel(detailRow.address_type)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="地址备注名">{detailRow.label || '-'}</Descriptions.Item>
            <Descriptions.Item label="联系人">{detailRow.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="公司">{detailRow.company || '-'}</Descriptions.Item>
            <Descriptions.Item label="地址第一行" span={2}>{detailRow.address_line || '-'}</Descriptions.Item>
            <Descriptions.Item label="地址第二行" span={2}>{detailRow.address_line2 || '-'}</Descriptions.Item>
            <Descriptions.Item label="城市">{detailRow.city || '-'}</Descriptions.Item>
            <Descriptions.Item label="州/省">{detailRow.state || '-'}</Descriptions.Item>
            <Descriptions.Item label="邮编">{detailRow.postal_code || '-'}</Descriptions.Item>
            <Descriptions.Item label="国家/地区">{countryLabel(detailRow.country)}</Descriptions.Item>
            <Descriptions.Item label="电话">{detailRow.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="Email">{detailRow.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="备注信息" span={2}>{detailRow.remarks || '-'}</Descriptions.Item>
            <Descriptions.Item label="证件类型">
              {detailRow.id_type ? (ID_TYPE_OPTIONS.find((o) => o.value === detailRow.id_type)?.label || detailRow.id_type) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="证件号码">
              {detailRow.id_number == null
                ? (detailRow.id_type ? <Tooltip title="无权限查看证件信息"><span style={{ color: '#bfbfbf' }}>无权限查看</span></Tooltip> : '-')
                : detailRow.id_number}
            </Descriptions.Item>
            <Descriptions.Item label="证件照片" span={2}>
              <Space size={8}>
                <IdPhotoThumb
                  size={48}
                  url={detailRow.id_photo_front_url}
                  emptyHint={detailRow.id_type ? '无权限查看或未上传' : undefined}
                  fetchBlob={() => downloadCustomerAddressIdPhoto(Number(detailRow.id), 'front').then((resp) => resp.data as Blob)}
                  onClick={() => handleViewPhoto(detailRow, 'front')}
                />
                <IdPhotoThumb
                  size={48}
                  url={detailRow.id_photo_back_url}
                  emptyHint={detailRow.id_type ? '无权限查看或未上传' : undefined}
                  fetchBlob={() => downloadCustomerAddressIdPhoto(Number(detailRow.id), 'back').then((resp) => resp.data as Blob)}
                  onClick={() => handleViewPhoto(detailRow, 'back')}
                />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="默认发件人">{detailRow.is_default_origin ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="默认收件人">{detailRow.is_default_recipient ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="状态">
              {detailRow.is_active === false ? <Tag color="red">停用</Tag> : <Tag color="green">启用</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="创建人">{detailRow.created_by_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{formatDateTime(detailRow.created_at)}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{formatDateTime(detailRow.updated_at)}</Descriptions.Item>
            {canPickCustomer && <Descriptions.Item label="所属客户" span={2}>{detailRow.customer_name || '-'}</Descriptions.Item>}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default CustomerAddressBookPage;
